
import { useState, useEffect } from 'react';
import { useCommentCount } from '@/contexts/comments/CommentCountContext';
import { useMetricsLoader } from './metrics/useMetricsLoader';
import { ContentType } from '@/services/comments/types';
import { useEntityRealtime } from './realtime/useEntityRealtime';

/**
 * Hook for fetching and managing social metrics for content with realtime updates using Universal Real-time Manager
 * Works with both polls and cards
 */
export const useSocialMetricsRealtime = (
  contentId: string,
  contentType: ContentType = 'card'
) => {
  // Try to access the comment count from context if available
  let commentContext;
  try {
    commentContext = useCommentCount();
  } catch (error) {
    // Context not available, will use local state
    commentContext = null;
  }

  // Load metrics and handle interaction with server
  const { 
    metrics, 
    isLoading, 
    loadMetrics,
    updateWithNewCommentCount 
  } = useMetricsLoader(contentId, contentType, commentContext?.commentCount);
  
  // Update metrics on load and when dependencies change
  useEffect(() => {
    if (contentId) {
      loadMetrics();
    }
  }, [contentId, contentType, loadMetrics]);
  
  // Setup realtime subscriptions using Universal Real-time Manager
  const { isConnected } = useEntityRealtime(
    contentType === 'poll' ? 'poll' : 'card',
    contentId,
    {
      onRelatedUpdate: () => {
        // Reload metrics when any related data changes
        loadMetrics();
      },
      
      onError: (error) => {
        console.error('Social metrics real-time error:', error);
      }
    },
    !!contentId // Only enabled if contentId is provided
  );
  
  // When comment count changes in context, update our metrics
  useEffect(() => {
    if (commentContext && metrics && commentContext.commentCount !== metrics.commentsCount) {
      updateWithNewCommentCount(commentContext.commentCount);
    }
  }, [commentContext?.commentCount, metrics, updateWithNewCommentCount]);

  return { 
    metrics, 
    isLoading, 
    updateSocialMetrics: loadMetrics,
    isRealtimeConnected: isConnected
  };
};
